import { useCallback, useMemo, useState, useEffect } from 'react';
import { Box, Typography, visuallyHidden } from 'bonfire-kindling';
import { Redirect } from 'react-router-dom';
import { withRouter, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import _isEqual from 'lodash/isEqual';

import { useDebounce } from 'helpers/hooks';
import { useVendor } from 'helpers/vendor';
import {
  useOpportunitiesActiveTab,
  useOpportunityTypes,
  useInfiniteOpportunitiesSearch,
  useInfiniteOpportunitiesSavedSearchProfile,
  useSpecificSavedSearchProfile,
} from 'helpers/opportunities';
import { SORT_BY, PROJECT_STATUS } from 'constants/opportunities';
import { allLocations } from 'constants/regions';
import { useIframeLogin } from '../hooks/useIframeLogin';
import { useOpportunitiesSearchFilter, useOpportunitiesLocations } from './hooks';
import { OpportunitiesProvider } from './OpportunitiesProvider';
import OpportunitiesTabs from './_Tabs/OpportunitiesTabs';
import OpportunitiesList from './_List/OpportunitiesList';
import OpportunitiesSearchBelt from './_Search/OpportunitiesSearchBelt';

const Opportunities = ({ match, location }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { tab: activeTab, id: routeSearchProfileId } = useOpportunitiesActiveTab();
  const [searchProfileId, setSearchProfileId] = useState(routeSearchProfileId);
  const { logInToOrganizationDomain, handleLoadGoToProjectIframe, iframeRef, iframeSrc } =
    useIframeLogin();

  const vendorQuery = useVendor();
  const opportunityTypesQuery = useOpportunityTypes();
  const searchProfileQuery = useSpecificSavedSearchProfile(searchProfileId, {
    onError: () => {
      history.push('/opportunities/recommended');
      setSearchProfileId(null);
    },
  });

  useEffect(() => {
    if (routeSearchProfileId !== null) {
      setSearchProfileId(routeSearchProfileId);
    }
  }, [routeSearchProfileId]);

  const {
    currentSearchFilter,
    currentSearchProfileFilter,
    effectiveFilter,
    setCurrentSearchProfileFilter,
    setEffectiveFilter,
    getInitialEffectiveFilter,
  } = useOpportunitiesSearchFilter({
    searchProfile: searchProfileQuery.data,
  });

  const debouncedCurrentSearchFilter = useDebounce(currentSearchFilter, 500);
  const debouncedCurrentSearchProfileFilter = useDebounce(currentSearchProfileFilter, 500);

  const commonEnabled =
    !!opportunityTypesQuery.data?.length &&
    !!vendorQuery.data &&
    !!debouncedCurrentSearchFilter.types.length;

  const recommendedOpportunities = useInfiniteOpportunitiesSearch(
    'recommended',
    debouncedCurrentSearchFilter,
    {
      enabled: commonEnabled,
    }
  );
  const followingOpportunities = useInfiniteOpportunitiesSearch(
    'following',
    debouncedCurrentSearchFilter,
    {
      enabled: commonEnabled,
    }
  );
  const allOpportunities = useInfiniteOpportunitiesSearch('all', debouncedCurrentSearchFilter, {
    enabled: commonEnabled,
  });
  const searchProfileOpportunities = useInfiniteOpportunitiesSavedSearchProfile(
    debouncedCurrentSearchProfileFilter.id,
    debouncedCurrentSearchProfileFilter,
    { enabled: !!debouncedCurrentSearchProfileFilter.id }
  );

  const onFilterChange = useCallback(
    filters => {
      if (filters.locations === null) {
        filters.locations = [...allLocations];
      }
      if (filters.statuses === PROJECT_STATUS.closed) {
        // Since we remove the "Closing Soon" option when filtering by closed
        // projects, the sort column also needs to be updated.
        filters.sortBy = SORT_BY.closingSoon;
      }

      setEffectiveFilter(prev => ({ ...prev, ...filters }));
    },
    [setEffectiveFilter]
  );

  const onSortChange = useCallback(
    e => {
      setEffectiveFilter(prev => ({ ...prev, sortBy: e.target.value }));
    },
    [setEffectiveFilter]
  );

  const onSearchChange = useCallback(
    search => {
      onFilterChange({ search });
    },
    [onFilterChange]
  );

  const { availableLocations, currentLocations } = useOpportunitiesLocations({
    searchProfileRegionData: searchProfileQuery.data?.regionData,
    vendorRegionData: vendorQuery.data?.regionData,
    effectiveFilter,
  });

  const initialEffectiveFilter = useMemo(
    () => getInitialEffectiveFilter(availableLocations),
    [availableLocations, getInitialEffectiveFilter]
  );

  const isSearchingOrFiltering = useMemo(
    () =>
      effectiveFilter.search !== '' ||
      effectiveFilter.statuses !== initialEffectiveFilter.statuses ||
      effectiveFilter.viewStatus !== initialEffectiveFilter.viewStatus ||
      effectiveFilter.sources !== initialEffectiveFilter.sources ||
      !_isEqual(effectiveFilter.types, initialEffectiveFilter.types) ||
      !_isEqual(effectiveFilter.organizations, initialEffectiveFilter.organizations) ||
      (activeTab === 'saved'
        ? !_isEqual(effectiveFilter.locations, initialEffectiveFilter.locations)
        : currentLocations.length !== availableLocations.length),
    [
      activeTab,
      availableLocations.length,
      currentLocations.length,
      effectiveFilter.locations,
      effectiveFilter.organizations,
      effectiveFilter.search,
      effectiveFilter.sources,
      effectiveFilter.statuses,
      effectiveFilter.types,
      effectiveFilter.viewStatus,
      initialEffectiveFilter.locations,
      initialEffectiveFilter.organizations,
      initialEffectiveFilter.sources,
      initialEffectiveFilter.statuses,
      initialEffectiveFilter.types,
      initialEffectiveFilter.viewStatus,
    ]
  );

  const onDeleteSearchProfile = useCallback(
    id => {
      if (searchProfileId === id) {
        setSearchProfileId(null);
        setCurrentSearchProfileFilter(curr => ({
          ...curr,
          id: null,
        }));
      }
      if (routeSearchProfileId === id) {
        history.push('/opportunities/recommended');
      }
    },
    [history, searchProfileId, routeSearchProfileId, setCurrentSearchProfileFilter]
  );

  const onSaveCurrentSearchProfile = useCallback(
    action => {
      const { taxonomyCodes, keywords, excludedKeywords } = searchProfileQuery.data ?? {};

      history.push({
        pathname:
          action === 'override' ? `/settings/saved/${searchProfileId}/edit` : '/settings/saved/new',
        state: {
          opportunitiesFilter: { ...effectiveFilter, taxonomyCodes, keywords, excludedKeywords },
        },
      });
    },
    [searchProfileQuery.data, history, searchProfileId, effectiveFilter]
  );

  let opportunitiesQuery = null;
  switch (activeTab) {
    case 'recommended':
      opportunitiesQuery = recommendedOpportunities;
      break;
    case 'following':
      opportunitiesQuery = followingOpportunities;
      break;
    case 'all':
      opportunitiesQuery = allOpportunities;
      break;
    case 'saved':
      opportunitiesQuery = searchProfileOpportunities;
      break;
    default:
      break;
  }

  if (match.isExact || !opportunitiesQuery) {
    return <Redirect to={{ ...location, pathname: '/opportunities/recommended' }} />;
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        sx={{ minHeight: '100%', bgcolor: 'background.main', width: '100%' }}
      >
        <Typography component="h1" style={visuallyHidden}>
          {t('opportunities.screen_reader.opportunities')}
        </Typography>
        <Box display="flex" mt={4} mb={2} mx={2}>
          <OpportunitiesTabs
            activeTab={activeTab}
            totalRecommended={recommendedOpportunities.total}
            totalFollowing={followingOpportunities.total}
            totalAll={allOpportunities.total}
            totalSaved={searchProfileOpportunities.total}
            currentSearchProfile={searchProfileQuery.data}
          />
        </Box>
        <OpportunitiesProvider
          onDeleteSearchProfile={onDeleteSearchProfile}
          onSaveCurrentSearchProfile={onSaveCurrentSearchProfile}
          isSearchingOrFiltering={isSearchingOrFiltering}
        >
          <Box m={2}>
            <OpportunitiesSearchBelt
              availableTypes={opportunityTypesQuery.data ?? []}
              availableLocations={availableLocations}
              currentLocations={currentLocations}
              currentFilter={effectiveFilter}
              initialFilter={initialEffectiveFilter}
              onFilterChange={onFilterChange}
              onSearchChange={onSearchChange}
              onSortChange={onSortChange}
            />
          </Box>
          <OpportunitiesList
            opportunitiesQuery={opportunitiesQuery}
            logInToOrganizationDomain={logInToOrganizationDomain}
          />
        </OpportunitiesProvider>
      </Box>
      {iframeSrc && (
        <iframe
          style={{ display: 'none' }}
          ref={iframeRef}
          title={t('opportunities.project.go_to_project')}
          src={iframeSrc}
          onLoad={handleLoadGoToProjectIframe}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default withRouter(Opportunities);
